import { Component, OnDestroy } from '@angular/core'
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { headShakeAnimation, shakeAnimation } from 'angular-animations'
import { MatCardModule } from '@angular/material/card'

import Panzoom, { PanzoomObject } from '@panzoom/panzoom'

import { InlineSVGModule } from 'ng-inline-svg-2'

import { log, MimeType, toArray } from '@nx-superprep/utils'
import { SafePipe } from '@nx-superprep/shared'
import { FrontendCard as Card, Choice, Resource, ChoiceType } from '@nx-superprep/backend/firebase'

import { GameplayBaseComponent } from '../gameplay-base'
import { SvgDiagramComponent } from './svg-diagram.component'

@Component({
  standalone: true,
  selector: 'gameplay-diagram',
  templateUrl: './diagram.component.html',
  styleUrls: ['./diagram.component.scss'],
  animations: [
    headShakeAnimation({ duration: 300, delay: 0 }),
    shakeAnimation({ duration: 200, delay: 0, translate: '3px' }),
  ],
  imports: [
    NgClass, NgTemplateOutlet, SafePipe,
    MatCardModule,
    InlineSVGModule,
    SvgDiagramComponent
  ],
})
export class DiagramComponent extends GameplayBaseComponent implements OnDestroy {
  override title = 'diagram'

  setAttrs = {
    width: '100%',
    height: '100%',
    xmlns: '',
    role: 'img',
  }

  delAttrs = [
    'xmlns',
    'title'
  ]

  diagram?: SVGElement
  background?: SVGElement

  private _choices: Choice[] = []
  private _questionSVG?: string
  private _loadedSVG?: SVGElement
  private _answerElement?: SVGElement
  private _zoomed?: PanzoomObject
  private _isZoomable = false;
  private _eventListeners: Array<{ element: Element, event: string, handler: EventListener }> = []
  private _svgParent?: Element;

  override initState(card: Card) {
    if (!card || !card.template.gameplay || (this.card && (!this.card?.template.diagram || !this.question))) {
      return false
    }

    this.choices = this._choices

    // update choice count before super.init in case svg added children choices
    if (card.template.gameplay.type === this.title && this._loadedSVG) {
      card.template.gameplay.choiceCount = this.choices.length
    }

    if (!super.initState(card) && !this._loadedSVG) {
      return false
    }

    this._questionSVG = this.question?.svg?.url

    if (!this._questionSVG) {
      return false
    }

    // update answerElement when card reuse the loaded svg
    if (this._choices.length > 0) {
      const _oldAnswer = this._choices.find(c => c.isAnswer)
      const _newAnswer = this._choices.find(c => c.name === card.template.answer?.name)

      if (_oldAnswer !== _newAnswer) {
        if (_oldAnswer) {
          _oldAnswer.type = ChoiceType.wrong
        }
        if (_newAnswer) {
          _newAnswer.type = ChoiceType.answer
        }
        this._answerElement = _newAnswer?.resources.find(r => r.name === _newAnswer.name)?.htmlElement as unknown as SVGElement
      }
    }

    if (!this.card) {
      return false
    }

    this.zoomToElement(
      this._zoomed,
      this._answerElement,
      this.card.template.diagram?.zoomInScale,
      {x: 0, y: 0}
    )

    return true
  }

  override resetState(card: Card | undefined) {
    this.setHighlight()
    if (this._zoomed) {
      this._zoomed.reset({ animate: false })
    }
    super.resetState(card)
  }

  zoomToElement(panZoom?: PanzoomObject, element?: Element, scale = 1, scrollTo?: {x:number, y:number}) {
    if (!panZoom) { return }

    const currentValues = panZoom.reset({ animate: false })
    log.debug(currentValues)

    if (!element) { return }

    setTimeout(() => {
      const initialBounds = element.getBoundingClientRect()
      const [clientX, clientY] = scrollTo
        ? [
            initialBounds.x + Math.max(0, Math.min(scrollTo.x, initialBounds.width)),
            initialBounds.y + Math.max(0, Math.min(scrollTo.y, initialBounds.height))
          ]
        : [
            initialBounds.x + initialBounds.width * 0.5,
            initialBounds.y + initialBounds.height * 0.5
          ]

      log.debug('panzoom:', clientX.toFixed(0), clientY.toFixed(0), scale.toFixed(2))
      const updatedValues = panZoom.zoomToPoint(scale, {clientX, clientY}, { animate: true })
      log.debug(updatedValues)
    }, 200)
  }

  onSVGInserted(svg: SVGElement) {
    if (!this.card || !this.answer) { return }

    const name = this.card.template.question?.svg?.name
    if (!name) { return }

    this.diagram = svg.querySelector(`#${name}`) as SVGElement || svg.children.namedItem(name) as SVGElement
    if (!this.diagram) { return }

    this.didAnswer = false
    this._choices = []

    const zoomInScale = this.card?.template.diagram?.zoomInScale ?? 1
    if (zoomInScale > 1 && !!svg.parentElement) {
      this._isZoomable = true
      this._zoomed = Panzoom(svg, {animate: true, duration: 500})
      this._svgParent = svg.parentElement;

      // Fixed type issue: Cast the wheel handler to EventListener
      if (this._svgParent && this._zoomed) {
        const wheelHandler = this._zoomed.zoomWithWheel as unknown as EventListener;
        this._svgParent.addEventListener('wheel', wheelHandler, { passive: false });
        this._eventListeners.push({
          element: this._svgParent,
          event: 'wheel',
          handler: wheelHandler
        });
      }
    } else {
      this._isZoomable = false
    }
    // Set ARIA attributes for accessibility
    svg.setAttribute('aria-label', 'Interactive diagram');

    // Avoid title popping up as a tooltip
    const title = svg.querySelector('title') || svg.children.item(0) as SVGTitleElement
    if (title) {
      title.innerHTML = this._isZoomable ? 'wheel to zoom' : ''
    }

    const backgroundElement = this.diagram.querySelector('#bg') as SVGElement
    if (backgroundElement) {
      this.background = backgroundElement
    }

    this.setupInteractiveElements();

    this._loadedSVG = svg
    this.initState(this.card)
  }

  private setupInteractiveElements() {
    if (!this.diagram || !this.card || !this.answer) return;

    const interactiveElements = Array.from(this.diagram.children)
      .filter(child => child.id !== 'bg') as SVGElement[];

    for (const group of interactiveElements) {
      const isAnswer = group.id === this.answer.name;
      group.setAttribute('class', isAnswer ? 'choice answer' : 'choice');

      // Add ARIA attributes for accessibility
      group.setAttribute('role', 'button');
      group.setAttribute('aria-label', group.id);
      group.setAttribute('tabindex', '0');

      const clickHandler = ((event: MouseEvent | KeyboardEvent) => {
        if (this.didAnswer || !this.card) { return }

        const element = event.currentTarget as SVGElement
        const _choice = this.card.template.choices.find((c) => c.name === element.id) ??
                        this.card.template.choices.find((c) => !c.isAnswer)

        if (!_choice) { return }

        this.chosen = _choice
        this.onForceAnswer(this.chosen.isAnswer)
      }) as EventListener;

      const mouseoverHandler = ((event: MouseEvent) => {
        if (this.didAnswer) { return }

        const element = event.currentTarget as SVGElement
        this.setHighlight(element.id)
      }) as EventListener;

      const mouseoutHandler = (() => {
        if (this.didAnswer) { return }

        this.setHighlight()
      }) as EventListener;

      const keydownHandler = ((event: KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          clickHandler(event);
        }
      }) as EventListener;

      group.addEventListener('click', clickHandler, { passive: true });
      group.addEventListener('mouseover', mouseoverHandler, { passive: true });
      group.addEventListener('mouseout', mouseoutHandler, { passive: true });
      group.addEventListener('keydown', keydownHandler);

      this._eventListeners.push(
        { element: group, event: 'click', handler: clickHandler },
        { element: group, event: 'mouseover', handler: mouseoverHandler },
        { element: group, event: 'mouseout', handler: mouseoutHandler },
        { element: group, event: 'keydown', handler: keydownHandler }
      );

      const resources = toArray(new Resource({
        key: 'html',
        name: group.id,
        mimeType: MimeType.text,
        htmlElement: group,
      }));

      const choice = new Choice({
        name: group.id,
        resources,
        type: isAnswer ? ChoiceType.answer : ChoiceType.wrong,
        mimeType: MimeType.text,
      });

      this._choices.push(choice);
      if (isAnswer) {
        this._answerElement = group;
      }
    }
  }

  override onForceAnswer(value = Math.random() < 0.5) {
    if (!this.card || !this.answer) { return }

    const choice = value
      ? this.answer
      : this.chosen ?? this.card.template.choices.find((c) => !c.isAnswer)

    if (!choice) { return }

    if (choice.isWrong) {
      this.zoomToElement(this._zoomed)
    }

    this.setHighlight(this.answer.name, 1)
    this.onSelect(choice)
  }

  setHighlight(name?: unknown, value = 1) {
    const dimmed = Number(name ? 1.2 - value : value).toString()

    if (this.background) {
      this.background.style.opacity = dimmed
    }

    this.setOpacity(this.choices, dimmed)

    if (!name) { return }

    const choice = this.choices.find(c => c.name === name)
    if (choice) {
      this.setOpacity([choice], value)
    }
  }

  setOpacity(choices: Choice | Choice[], value: string | number) {
    choices = toArray(choices)

    if (choices.length === 0) {
      return
    }

    const opacity = Number(value).toString()

    choices
      .map((c) => c.html?.htmlElement)
      .filter(h => h)
      .forEach((c) => {
        if (c?.style) {
          c.style.opacity = opacity
          c.style.cursor = 'pointer'
        }
      })
  }

  get wrongs() {
    return this.choices.filter((c) => !c.isAnswer)
  }

  questionSVG() {
    return this._questionSVG ?? ''
  }

  override ngOnDestroy() {
    super.ngOnDestroy()

    // Clean up all event listeners
    this._eventListeners.forEach(({ element, event, handler }) => {
      element.removeEventListener(event, handler)
    })

    // Reset the zoomed state to prevent memory leaks
    if (this._zoomed) {
      this._zoomed.destroy()
      this._zoomed = undefined
    }

    // Clear the array
    this._eventListeners = []
  }
}
